'use client'

import { useImageProvider } from '@7f-ui'
import { Card, CardDetails, CardTitle } from '@7f-ui/components/card/card'
import { CardImage } from '@7f-ui/components/card/card-image'
import type { Image } from '@data-access/graphql/schema.types'
import { useInvalidateIsOnWishlist } from '@feat/feat-wishlists/status'
import { WishlistToggle } from '@feat/feat-wishlists/toggle'
import { appendSearchParams } from '@utils/helpers'
import type { PropsWithChildren } from 'react'
import { memo, useMemo } from 'react'

import { useExperienceURL } from '../hooks/use-experience-data'
import { ExperienceCardFlag } from './experience-card-flag'
import { ExperienceCardPrice } from './experience-card-price'
import { ExperienceCardRating } from './experience-card-rating'
import { ExperienceSubtitle } from './experience-subtitle'

type OnClickProps = {
  event: React.MouseEvent<HTMLAnchorElement>
  href: string
}

type Experience = {
  id: string
  publicId: number
  slug: string
  title: string
  location: string
  duration?: number | null
  hasMultipleDurations: boolean
  hasTimeslots?: boolean
  priceCategory?: string | null
  hasMultiplePrices: boolean
  currency?: string
  images?: Image[] | null
  price?: number | null
}

type ExperienceCardProps = PropsWithChildren<{
  experience: Experience
  linkParams?: Record<string, unknown>
  isFixedWidth?: boolean
  showSubtitle?: boolean
  disableSlider?: boolean
  reduceTransitions?: boolean
  onClick?: ({ event, href }: OnClickProps) => void
}>

const ExperienceCard = memo(
  ({
    experience,
    linkParams,
    isFixedWidth,
    onClick,
    showSubtitle = true,
    disableSlider = false,
    reduceTransitions = false,
    ...rest
  }: ExperienceCardProps) => {
    const { logo } = useImageProvider()
    const experienceURL = useExperienceURL(experience)
    const href = linkParams ? appendSearchParams(experienceURL, linkParams) : experienceURL
    const invalidateIsOnWishlist = useInvalidateIsOnWishlist()

    const images = useMemo(
      () =>
        experience?.images?.map(image => ({
          url: image.url,
          alt: image.alt || experience.title,
        })) ?? [],

      [experience.images, experience.title]
    )

    return (
      <div className="relative isolate">
        <div className="absolute top-4 right-3 z-10 inline-flex">
          <WishlistToggle
            iconOnly
            productId={experience.id}
            publicProductId={experience.publicId}
            productType="experience"
            productName={experience.title}
            locationName={experience.location}
            image={experience.images?.[0]?.url}
            duration={experience.duration}
            origin="list"
            onAdd={invalidateIsOnWishlist}
          />
        </div>

        <Card
          {...rest}
          href={href}
          isFixedWidth={isFixedWidth}
          onClick={event => {
            onClick?.({ event, href })
          }}
          data-experience-id={experience.id}
        >
          <CardImage
            images={images}
            title={experience.title}
            disableSlider={disableSlider}
            reduceTransitions={reduceTransitions}
            aspectRatio="video"
            fallbackImageSrc={logo.fallbackSrc}
          />
          {experience.hasTimeslots === false && <ExperienceCardFlag />}

          <CardDetails>
            <div className="grid gap-2">
              <div className="grid gap-1">
                <ExperienceCardRating publicId={experience.publicId} />
                <CardTitle title={experience.title} />
                {showSubtitle && (
                  <ExperienceSubtitle
                    locationName={experience.location}
                    duration={experience.duration}
                    hasMultipleDurations={experience.hasMultipleDurations}
                  />
                )}
              </div>
              {!!experience.price && (
                <ExperienceCardPrice
                  price={experience.price}
                  priceCategory={experience.priceCategory}
                  hasMultiplePrices={experience.hasMultiplePrices}
                  currency={experience.currency}
                />
              )}
            </div>
          </CardDetails>
        </Card>
      </div>
    )
  }
)

ExperienceCard.displayName = 'ExperienceCard'

export { ExperienceCard }
